import React, { useMemo } from 'react';
import { StrictReactNode } from '../../types/StrictReactNode';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import {
  ModalWithBackdrop,
  ModalWithBackdropProps
} from '../ModalWithBackdrop/ModalWithBackdrop';
import styles from './BaseDialog.module.css';
import { BaseDialogTabTitle } from './BaseDialogTabTitle/BaseDialogTabTitle';

export type BaseDialogProps<TAnyTabName extends string> = {
  opened: boolean;
  activeTabName: TAnyTabName;
  /**
   * Makes the content container as high as possible (there will still be space between the screen edges and the dialog).
   * Also puts the content into a flex column layout
   */
  fullHeight?: boolean;
  renderButtons: () => Array<StrictReactNode>;
  onTabSelected?: (name: TAnyTabName) => void;
  onBackdropClick?: ModalWithBackdropProps['onBackdropClick'];
  children: () => { [key in TAnyTabName]: BaseDialogTab<key> };
  'data-testid'?: string;
};

export type BaseDialogTab<TName extends string> = {
  name: TName;
  title: string;
  renderContent: () => StrictReactNode;
};

// TODO:
export const BaseDialog = React.memo(
  <TAnyTabName extends string>(props: BaseDialogProps<TAnyTabName>) => {
    const {
      opened,
      renderButtons,
      onBackdropClick,
      onTabSelected,
      activeTabName,
      'data-testid': dataTestId,
      children,
      fullHeight = false
    } = prepareProps({ props });

    const tabConfiguration = useMemo(() => {
      return children();
    }, [children]);

    const tabs = useMemo(() => {
      return Object.values(tabConfiguration) as Array<
        BaseDialogTab<TAnyTabName>
      >;
    }, [tabConfiguration]);

    const renderedTabTitles: Array<StrictReactNode> = useMemo(() => {
      return tabs.map<StrictReactNode>((tab) => {
        return (
          <BaseDialogTabTitle<TAnyTabName>
            name={tab.name}
            title={tab.title}
            selected={tab.name === activeTabName}
            key={tab.name}
            onSelected={onTabSelected}
          ></BaseDialogTabTitle>
        );
      });
    }, [activeTabName, tabs, onTabSelected]);

    const renderedTabContent: StrictReactNode = useMemo(() => {
      return tabConfiguration[activeTabName].renderContent();
    }, [activeTabName, tabConfiguration]);

    const renderedButtons: StrictReactNode = useMemo(() => {
      return renderButtons();
    }, [renderButtons]);

    return (
      <ModalWithBackdrop
        opened={opened}
        onBackdropClick={onBackdropClick}
        data-testid={dataTestId}
      >
        <div className={styles.headerBar}>
          <div
            className={styles.tabTitles}
            data-testid="BaseDialog.TabTitles"
          >
            {renderedTabTitles}
          </div>
          <div>{renderedButtons}</div>
        </div>
        <div
          className={classes(
            styles.content,
            fullHeight ? styles.fullHeightContent : null
          )}
        >
          {renderedTabContent}
        </div>
      </ModalWithBackdrop>
    );
  }
) as <TAnyTabName extends string>(
  props: BaseDialogProps<TAnyTabName>
) => JSX.Element;
